import React, { useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from './Button';

function fmt(d) {
  return new Date(d).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
}

export default function MatchChatBox({ matchId, canWrite }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = () => {
    api.get(`/api/matches/${matchId}/chat`)
      .then((r) => setMessages(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [matchId]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/matches/${matchId}/chat`, { message: input.trim() });
      setInput('');
      load();
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-600">Match-Chat</span>
        <span className="text-xs text-slate-400">Aktualisiert alle 5 Sekunden</span>
      </div>

      <div className="flex-1 overflow-y-auto max-h-64 p-3 space-y-2 bg-white">
        {messages.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">
            Noch keine Nachrichten. Koordiniert hier wann und wo ihr spielt!
          </p>
        )}
        {messages.map((msg) => {
          const isMe = user && msg.user_id === user.id;
          const isAdminMsg = msg.user?.role === 'admin' || msg.user?.role === 'superadmin';
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm
                ${isMe
                  ? 'bg-ocean-500 text-white rounded-br-none'
                  : isAdminMsg
                    ? 'bg-red-50 border border-red-200 text-slate-800 rounded-bl-none'
                    : 'bg-slate-100 text-slate-800 rounded-bl-none'}
              `}>
                {!isMe && (
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-[10px] font-bold opacity-70">
                      {msg.user?.nickname || msg.user?.username || 'Unbekannt'}
                    </span>
                    {isAdminMsg && (
                      <span className="text-[9px] font-bold bg-red-500 text-white px-1 py-0.5 rounded uppercase tracking-wide">
                        Admin
                      </span>
                    )}
                  </div>
                )}
                {isMe && isAdminMsg && (
                  <div className="text-[9px] font-bold bg-white/30 text-white px-1 py-0.5 rounded uppercase tracking-wide inline-block mb-1">
                    Admin
                  </div>
                )}
                <div>{msg.message}</div>
                <div className="text-[10px] mt-0.5 opacity-60 text-right">
                  {fmt(msg.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {canWrite ? (
        <form onSubmit={send} className="flex gap-2 p-3 border-t border-slate-100 bg-white">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Nachricht..."
            className="input flex-1 text-sm"
            maxLength={500}
            disabled={sending}
          />
          <Button type="submit" size="sm" loading={sending} disabled={!input.trim()}>
            Senden
          </Button>
        </form>
      ) : (
        <div className="text-xs text-slate-400 text-center py-2 border-t border-slate-100 bg-slate-50">
          {user ? 'Nur Team-Mitglieder können schreiben' : 'Einloggen um zu schreiben'}
        </div>
      )}
    </div>
  );
}
